import { GalleryViewMedia } from '../../types/galleryTypes';
import { useEffect, useState } from 'react';
import { useOnUpdateOnly } from '../../../../../hooks/UseOnUpdateOnly';
import { UseGalleryModalArgs } from './GalleryView.typed';
import type { SwiperClass } from 'swiper/react';

export const GALLERY_INITIAL_MEDIA_INDEX_LAST_FLAG = -1;

const useGalleryModal = ({
  media: defaultMedia,
  askToClose,
  photos,
  link360,
  link360Interior,
  videoConfig,
  isOpen,
  initialMediaIndex,
  imageCounterForDFP,
  setImageCounterForDFP,
  refreshTopAndSideDFPUnit,
}: UseGalleryModalArgs) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);

  const [currentMediaIndex, setCurrentMediaIndex] = useState<number>(
    initialMediaIndex || 0,
  );
  const [media, setMedia] = useState<GalleryViewMedia[]>(defaultMedia);

  const [above1024, setScreen] = useState<boolean>(false);

  useEffect(() => {
    const isAbove1024 = window.matchMedia('(min-width: 1024px)');
    setScreen(isAbove1024.matches);
  }, []);

  useEffect(() => {
    // Check if we want to open the gallery on the last slide
    if (currentMediaIndex === GALLERY_INITIAL_MEDIA_INDEX_LAST_FLAG) {
      setCurrentMediaIndex(media.length - 1);
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, link360, link360Interior, videoConfig, isOpen]);

  useEffect(() => {
    if (typeof initialMediaIndex === 'number') {
      if (initialMediaIndex !== GALLERY_INITIAL_MEDIA_INDEX_LAST_FLAG) {
        setCurrentMediaIndex(initialMediaIndex);
      }
    }
  }, [initialMediaIndex]);

  /**
   * Keep track of which slides have been viewed
   */
  useOnUpdateOnly(() => {
    let copyOfMediaArray = [...media];
    let galleryItem = {
      ...copyOfMediaArray[currentMediaIndex],
      viewed: true,
    };
    copyOfMediaArray[currentMediaIndex] = galleryItem;
    setMedia(copyOfMediaArray);
  }, [currentMediaIndex, isOpen]);

  function handleCloseRequest() {
    /**
     * Build up usage data to report on.
     */
    const numberOfPhotosViewed = media.filter(
      (mediaItem) => mediaItem.viewed,
    ).length;
    const hasVideoBeenViewed = media.filter(
      (mediaItem) => mediaItem.viewed && mediaItem.isVideo,
    );
    const has360BeenViewed = media.filter(
      (mediaItem) => mediaItem.viewed && mediaItem.is360,
    );
    setImageCounterForDFP && imageCounterForDFP && setImageCounterForDFP(0);
    refreshTopAndSideDFPUnit && refreshTopAndSideDFPUnit();

    askToClose({
      numberOfPhotos: media.length,
      numberOfPhotosViewed,
      videoImpression: !!hasVideoBeenViewed.length,
      threeSixtyImpression: !!has360BeenViewed.length,
    });
  }

  return {
    media,
    thumbsSwiper,
    above1024,
    currentMediaIndex,
    setCurrentMediaIndex,
    setThumbsSwiper,
    handleCloseRequest,
  };
};

export { useGalleryModal };
